import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../Auth/AuthContext';
import {listProfiles, deleteProfile as deleteProfileApi, updateProfile as updateProfileApi, createProfile as createProfileApi, submitForReview, toggleVisibility} from '../../utils/designerApi';
import {toast} from '../Toast';

// Icons
const PlusIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="5" x2="12" y2="19"/>
        <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);

const EditIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);

const MoreIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="1"/>
        <circle cx="19" cy="12" r="1"/>
        <circle cx="5" cy="12" r="1"/>
    </svg>
);

const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
);

const CopyIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
);

// Profile Preview Card Component - Shows a dynamic mini preview of the actual profile content
function ProfilePreviewCard({profile}) {
    const settings = profile.settings || {};
    const editorContent = profile.editorContent || [];
    const backgroundColor = settings.backgroundColor || '#ffffff';

    // Determine if background is dark for text contrast
    const isDarkBg = (color) => {
        if (!color || color === 'transparent') {return false;}
        const hex = color.replace('#', '');
        if (hex.length !== 6) {return false;}
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness < 128;
    };

    const isDark = isDarkBg(backgroundColor);
    const textColor = isDark ? '#ffffff' : '#1a1a1a';
    const subtextColor = isDark ? 'rgba(255,255,255,0.7)' : '#666666';

    // Render mini version of each block type
    const renderMiniBlock = (block, index) => {
        if (!block || !block.type) {return null;}
        const props = block.props || {};

        switch (block.type) {
        case 'profileHeader':
            return (
                <div key={index} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginBottom: '4px'
                }}>
                    {/* Avatar */}
                    <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        marginBottom: '3px',
                        border: isDark ? '1.5px solid rgba(255,255,255,0.3)' : '1.5px solid rgba(0,0,0,0.08)',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                    }}>
                        {props.avatarUrl ? (
                            <img src={props.avatarUrl} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                        ) : (
                            <div style={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : '#e8e8e8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                fontWeight: '600',
                                color: textColor
                            }}>
                                {(props.name || 'U').charAt(0).toUpperCase()}
                            </div>
                        )}
                    </div>
                    {/* Name */}
                    {props.name && (
                        <div style={{
                            fontSize: '7px',
                            fontWeight: '600',
                            color: textColor,
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            lineHeight: '1.2'
                        }}>
                            {props.name}
                        </div>
                    )}
                    {/* Bio */}
                    {props.bio && (
                        <div style={{
                            fontSize: '5px',
                            color: subtextColor,
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            lineHeight: '1.2',
                            marginTop: '1px'
                        }}>
                            {props.bio}
                        </div>
                    )}
                </div>
            );

        case 'linkButton':
            const isFilled = props.style === 'filled';
            return (
                <div key={index} style={{
                    height: '8px',
                    backgroundColor: isFilled ? (isDark ? 'rgba(255,255,255,0.9)' : '#1a1a1a') : 'transparent',
                    border: isFilled ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.2)'}`,
                    borderRadius: '4px',
                    width: props.size === 'small' ? '60%' : '90%',
                    margin: '1.5px auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {props.text && (
                        <span style={{
                            fontSize: '4px',
                            fontWeight: '500',
                            color: isFilled ? (isDark ? '#1a1a1a' : '#ffffff') : textColor,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '90%'
                        }}>
                            {props.text}
                        </span>
                    )}
                </div>
            );

        case 'sectionTitle':
            return (
                <div key={index} style={{
                    fontSize: '5px',
                    fontWeight: '600',
                    color: textColor,
                    textAlign: 'center',
                    margin: '3px 0 1px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%'
                }}>
                    {props.title || 'Section'}
                </div>
            );

        case 'socialLinks':
            const socialIcons = [];
            if (props.instagram) {socialIcons.push('ig');}
            if (props.twitter) {socialIcons.push('tw');}
            if (props.tiktok) {socialIcons.push('tk');}
            if (props.youtube) {socialIcons.push('yt');}
            if (props.linkedin) {socialIcons.push('li');}
            if (props.facebook) {socialIcons.push('fb');}
            if (socialIcons.length === 0) {return null;}
            return (
                <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '2px',
                    margin: '2px 0'
                }}>
                    {socialIcons.slice(0, 5).map((icon, i) => (
                        <div key={i} style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.1)'
                        }} />
                    ))}
                </div>
            );

        case 'gallery':
            const images = props.images || [];
            if (images.length === 0) {return null;}
            return (
                <div key={index} style={{
                    display: 'flex',
                    gap: '2px',
                    justifyContent: 'center',
                    margin: '2px 0',
                    flexWrap: 'wrap'
                }}>
                    {images.slice(0, 4).map((img, i) => (
                        <div key={i} style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '2px',
                            overflow: 'hidden',
                            backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#f0f0f0'
                        }}>
                            {img.url && (
                                <img src={img.url} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                            )}
                        </div>
                    ))}
                </div>
            );

        case 'productCard':
            return (
                <div key={index} style={{
                    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.03)',
                    borderRadius: '3px',
                    padding: '3px',
                    margin: '1.5px 3px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                }}>
                    <div style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '2px',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : '#e8e8e8',
                        overflow: 'hidden',
                        flexShrink: 0
                    }}>
                        {props.imageUrl && (
                            <img src={props.imageUrl} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                        )}
                    </div>
                    <div style={{flex: 1, minWidth: 0}}>
                        <div style={{
                            fontSize: '4px',
                            fontWeight: '600',
                            color: textColor,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {props.productName || 'Product'}
                        </div>
                        {props.price && (
                            <div style={{fontSize: '4px', color: subtextColor}}>
                                {props.price}
                            </div>
                        )}
                    </div>
                </div>
            );

        case 'shoppablePost':
        case 'shoppableVideo':
            return (
                <div key={index} style={{
                    width: '75%',
                    height: '20px',
                    margin: '1.5px auto',
                    borderRadius: '3px',
                    overflow: 'hidden',
                    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#f0f0f0'
                }}>
                    {props.imageUrl && (
                        <img src={props.imageUrl} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                    )}
                </div>
            );

        case 'productGallery':
            // Parse items from JSON string
            let galleryItems = [];
            try {
                galleryItems = JSON.parse(props.items || '[]');
            } catch (e) {
                galleryItems = [];
            }
            if (galleryItems.length === 0) {return null;}
            const isGridLayout = props.layout === 'grid';
            return (
                <div key={index} style={{margin: '2px 0'}}>
                    {props.title && (
                        <div style={{
                            fontSize: '5px',
                            fontWeight: '600',
                            color: textColor,
                            marginBottom: '2px'
                        }}>
                            {props.title}
                        </div>
                    )}
                    <div style={isGridLayout ? {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '2px'
                    } : {
                        display: 'flex',
                        gap: '2px',
                        overflowX: 'hidden'
                    }}>
                        {galleryItems.slice(0, isGridLayout ? 4 : 3).map((item, i) => (
                            <div key={i} style={{
                                width: isGridLayout ? 'auto' : '24px',
                                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
                                borderRadius: '3px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: isGridLayout ? '100%' : '24px',
                                    height: isGridLayout ? '16px' : '18px',
                                    overflow: 'hidden'
                                }}>
                                    {item.imageUrl && (
                                        <img src={item.imageUrl} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                    )}
                                </div>
                                <div style={{padding: '2px'}}>
                                    <div style={{
                                        fontSize: '3px',
                                        fontWeight: '600',
                                        color: textColor,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {item.name}
                                    </div>
                                    <div style={{
                                        fontSize: '3px',
                                        color: '#ff6b6b',
                                        fontWeight: '600'
                                    }}>
                                        {item.price}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );

        case 'askAI':
            return (
                <div key={index} style={{
                    height: '8px',
                    backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
                    borderRadius: '4px',
                    width: '85%',
                    margin: '1.5px auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px'
                }}>
                    <div style={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.2)'
                    }} />
                    <span style={{
                        fontSize: '4px',
                        color: subtextColor
                    }}>
                        Ask AI
                    </span>
                </div>
            );

        case 'paragraph':
            // Skip empty paragraphs
            if (!props.text && (!block.content || block.content.length === 0)) {return null;}
            return null; // Don't show paragraphs in preview to save space

        default:
            return null;
        }
    };

    // Get all blocks to render (show more content)
    const blocksToRender = editorContent.slice(0, 12);

    return (
        <div style={{
            width: '100%',
            height: '100%',
            backgroundColor: backgroundColor,
            borderRadius: '6px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '6px 4px',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)'
        }}>
            {blocksToRender.length > 0 ? (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0px',
                    overflow: 'hidden'
                }}>
                    {blocksToRender.map((block, index) => renderMiniBlock(block, index))}
                </div>
            ) : (
                // Fallback for empty profiles
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}>
                    <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : '#e8e8e8',
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: textColor
                    }}>
                        {(settings.displayName || profile.name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div style={{
                        fontSize: '7px',
                        fontWeight: '600',
                        color: textColor,
                        textAlign: 'center'
                    }}>
                        {settings.displayName || profile.name || 'Untitled'}
                    </div>
                </div>
            )}
        </div>
    );
}

// Skeleton loading card
function SkeletonProfileCard() {
    const shimmerStyle = {
        background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        borderRadius: '4px'
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: '20px 24px',
                backgroundColor: '#ffffff',
                border: '1px solid #e8e8e8',
                borderRadius: '12px'
            }}
        >
            {/* Preview Thumbnail Skeleton */}
            <div style={{
                width: '120px',
                height: '140px',
                borderRadius: '8px',
                marginRight: '24px',
                flexShrink: 0,
                border: '1px solid rgba(0,0,0,0.08)',
                ...shimmerStyle
            }} />

            {/* Profile Info Skeleton */}
            <div style={{flex: 1, minWidth: 0}}>
                <div style={{
                    width: '200px',
                    height: '16px',
                    marginBottom: '8px',
                    ...shimmerStyle
                }} />
                <div style={{
                    width: '300px',
                    height: '13px',
                    marginBottom: '12px',
                    ...shimmerStyle
                }} />
                <div style={{
                    width: '80px',
                    height: '24px',
                    borderRadius: '12px',
                    ...shimmerStyle
                }} />
            </div>

            {/* Actions Skeleton */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginLeft: '20px'
            }}>
                <div style={{
                    width: '90px',
                    height: '36px',
                    borderRadius: '6px',
                    ...shimmerStyle
                }} />
                <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    ...shimmerStyle
                }} />
            </div>
        </div>
    );
}

// Rename Icon
const RenameIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
    </svg>
);

// Share Link Icon
const ShareIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="18" cy="5" r="3"/>
        <circle cx="6" cy="12" r="3"/>
        <circle cx="18" cy="19" r="3"/>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
);

// Send for Review Icon
const SendIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
);

export default function MyPages() {
    const navigate = useNavigate();
    const {tokens, user} = useAuth();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [menuOpen, setMenuOpen] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [showRenameModal, setShowRenameModal] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [shareLink, setShareLink] = useState(null);
    const [shareCopied, setShareCopied] = useState(false);
    const [showNewPageModal, setShowNewPageModal] = useState(false);
    const [newPageName, setNewPageName] = useState('');

    // Load profiles from API
    const loadProfiles = useCallback(async () => {
        if (!tokens?.accessToken) {return;}
        setLoading(true);
        const result = await listProfiles({accessToken: tokens.accessToken});
        if (result.ok && result.data?.profiles) {
            setProfiles(result.data.profiles);
        }
        setLoading(false);
    }, [tokens?.accessToken]);

    useEffect(() => {
        loadProfiles();
    }, [loadProfiles]);

    const handleCreateNewPage = () => {
        setNewPageName('');
        setShowNewPageModal(true);
    };

    const handleConfirmNewPage = async () => {
        const pageName = newPageName.trim() || 'My Profile';
        const slug = pageName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

        setActionLoading('new');
        const result = await createProfileApi({
            accessToken: tokens?.accessToken,
            name: pageName,
            slug: slug,
            settingsJson: {displayName: pageName},
            editorState: null,
            contentHtml: null,
            contentPlain: null,
            contentMarkdown: null
        });

        setActionLoading(null);
        setShowNewPageModal(false);
        setNewPageName('');

        if (result.ok && result.data?.profile?.id) {
            toast.success(`Page "${pageName}" created successfully!`);
            localStorage.setItem('blocknote-designer-active-profile', String(result.data.profile.id));
            navigate('/designer');
        } else {
            toast.error(result.error || 'Failed to create page');
            console.error('Failed to create profile:', result.error);
        }
    };

    const handleEditPage = (profileId) => {
        localStorage.setItem('blocknote-designer-active-profile', String(profileId));
        navigate('/designer');
    };

    const handleDeleteProfile = async (profileId) => {
        if (profiles.length <= 1) {
            return;
        }
        setActionLoading(profileId);
        const result = await deleteProfileApi({accessToken: tokens?.accessToken, id: profileId});
        if (result.ok) {
            await loadProfiles();
        } else {
            console.error('Failed to delete profile:', result.error);
        }
        setActionLoading(null);
        setShowDeleteConfirm(null);
        setMenuOpen(null);
    };

    const handleDuplicateProfile = async (profile) => {
        setActionLoading(profile.id);
        const result = await createProfileApi({
            accessToken: tokens?.accessToken,
            name: `${profile.name} (Copy)`,
            slug: null,
            settingsJson: profile.settingsJson,
            editorState: profile.editorState,
            contentHtml: profile.contentHtml,
            contentPlain: profile.contentPlain,
            contentMarkdown: profile.contentMarkdown
        });
        if (result.ok) {
            await loadProfiles();
        } else {
            console.error('Failed to duplicate profile:', result.error);
        }
        setActionLoading(null);
        setMenuOpen(null);
    };

    const handleOpenRename = (profile) => {
        const settings = profile.settingsJson || {};
        setRenameValue(settings.displayName || profile.name || '');
        setShowRenameModal(profile.id);
        setMenuOpen(null);
    };

    const handleRenameProfile = async () => {
        if (!renameValue.trim()) {return;}
        setActionLoading(showRenameModal);

        const profile = profiles.find(p => p.id === showRenameModal);
        const newName = renameValue.trim();
        const newSlug = newName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const updatedSettings = {...(profile?.settingsJson || {}), displayName: newName};

        const result = await updateProfileApi({
            accessToken: tokens?.accessToken,
            id: showRenameModal,
            name: newName,
            slug: newSlug,
            settingsJson: updatedSettings
        });
        if (result.ok) {
            await loadProfiles();
        } else {
            console.error('Failed to rename profile:', result.error);
        }
        setActionLoading(null);
        setShowRenameModal(null);
        setRenameValue('');
    };

    const handleSubmitForReview = async (profileId) => {
        setActionLoading(profileId);
        const result = await submitForReview({accessToken: tokens?.accessToken, id: profileId});
        if (result.ok) {
            await loadProfiles();
        } else {
            console.error('Failed to submit for review:', result.error);
        }
        setActionLoading(null);
        setMenuOpen(null);
    };

    const handleToggleVisibility = async (profile) => {
        setActionLoading(profile.id);
        const result = await toggleVisibility({
            accessToken: tokens?.accessToken,
            id: profile.id,
            isPublic: !profile.isPublic
        });
        if (result.ok) {
            await loadProfiles();
        } else {
            console.error('Failed to toggle visibility:', result.error);
        }
        setActionLoading(null);
        setMenuOpen(null);
    };

    const handleShowShareLink = (profile) => {
        if (!profile.slug) {return;}
        const baseUrl = window.location.origin + window.location.pathname;
        setShareLink(`${window.location.origin}/${profile.slug}`);
        setShareCopied(false);
        setMenuOpen(null);
    };

    const handleCopyShareLink = () => {
        if (shareLink) {
            navigator.clipboard.writeText(shareLink).then(() => {
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 2000);
            });
        }
    };

    const getProfileUrl = (profile) => {
        const slug = profile.slug || profile.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'profile';
        const baseUrl = window.location.origin + window.location.pathname;
        return `${window.location.origin}/${slug}`;
    };

    const getStatusConfig = (status) => {
        switch (status) {
        case 'published':
            return {label: 'Published', bg: '#e6f4ea', color: '#1e7e34'};
        case 'pending_review':
            return {label: 'Pending Review', bg: '#fff3cd', color: '#856404'};
        case 'rejected':
            return {label: 'Rejected', bg: '#f8d7da', color: '#721c24'};
        case 'draft':
        default:
            return {label: 'Draft', bg: '#f5f5f5', color: '#666666'};
        }
    };

    // Build a localStorage-compatible preview object from the API profile
    const toPreviewProfile = (profile) => ({
        id: profile.id,
        name: profile.name,
        settings: profile.settingsJson || {},
        editorContent: profile.editorState?.document || profile.editorState || []
    });

    return (
        <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '24px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        }}>
            {/* Breadcrumb */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                color: '#666',
                marginBottom: '16px'
            }}>
                <span>My Pages</span>
            </div>

            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '32px'
            }}>
                <h1 style={{
                    fontSize: '28px',
                    fontWeight: '600',
                    color: '#37352f',
                    margin: 0
                }}>
                    Profiles
                </h1>

                <button
                    onClick={handleCreateNewPage}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#37352f',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f7f7f5';
                        e.currentTarget.style.borderColor = '#d0d0d0';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ffffff';
                        e.currentTarget.style.borderColor = '#e0e0e0';
                    }}
                >
                    <PlusIcon />
                    Create New Page
                </button>
            </div>

            {/* Share Link Modal */}
            {shareLink && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.4)',
                            zIndex: 1000
                        }}
                        onClick={() => setShareLink(null)}
                    />
                    <div style={{
                        position: 'fixed',
                        top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        padding: '24px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                        zIndex: 1001,
                        width: '480px',
                        maxWidth: '90vw'
                    }}>
                        <h3 style={{fontSize: '18px', fontWeight: '600', color: '#37352f', marginTop: 0, marginBottom: '12px'}}>
                            Share Public Link
                        </h3>
                        <p style={{fontSize: '14px', color: '#666', marginBottom: '16px'}}>
                            Anyone with this link can view the published version of your profile.
                        </p>
                        <div style={{display: 'flex', gap: '8px'}}>
                            <input
                                type="text"
                                value={shareLink}
                                readOnly
                                style={{
                                    flex: 1,
                                    padding: '10px 14px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    color: '#37352f',
                                    backgroundColor: '#f7f7f5',
                                    outline: 'none'
                                }}
                            />
                            <button
                                onClick={handleCopyShareLink}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: shareCopied ? '#1e7e34' : '#37352f',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#ffffff',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {shareCopied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '16px'}}>
                            <button
                                onClick={() => setShareLink(null)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f5f5f5',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    color: '#37352f',
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* New Page Name Modal */}
            {showNewPageModal && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.4)',
                            zIndex: 1000
                        }}
                        onClick={() => {
                            setShowNewPageModal(false);
                            setNewPageName('');
                        }}
                    />
                    <div style={{
                        position: 'fixed',
                        top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        padding: '24px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                        zIndex: 1001,
                        width: '400px',
                        maxWidth: '90vw'
                    }}>
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#37352f',
                            marginTop: 0,
                            marginBottom: '16px'
                        }}>
                            Create New Page
                        </h3>
                        <p style={{
                            fontSize: '14px',
                            color: '#666666',
                            marginBottom: '16px'
                        }}>
                            Enter a name for your new page
                        </p>
                        <input
                            type="text"
                            value={newPageName}
                            onChange={(e) => setNewPageName(e.target.value)}
                            placeholder="My Profile"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleConfirmNewPage();
                                }
                            }}
                            style={{
                                width: '100%',
                                padding: '12px 14px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                fontSize: '15px',
                                outline: 'none',
                                marginBottom: '20px',
                                boxSizing: 'border-box'
                            }}
                        />
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px'
                        }}>
                            <button
                                onClick={() => {
                                    setShowNewPageModal(false);
                                    setNewPageName('');
                                }}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#f5f5f5',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#37352f',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmNewPage}
                                disabled={actionLoading === 'new'}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#37352f',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#ffffff',
                                    cursor: actionLoading === 'new' ? 'wait' : 'pointer'
                                }}
                            >
                                {actionLoading === 'new' ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Profile Cards List */}
            {loading ? (
                <>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                    }}>
                        {Array.from({length: 5}).map((_, i) => (
                            <SkeletonProfileCard key={i} />
                        ))}
                    </div>
                    <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>
                </>
            ) : profiles.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '80px 20px',
                    color: '#91918e'
                }}>
                    <div style={{fontSize: '48px', marginBottom: '16px'}}>📄</div>
                    <div style={{fontSize: '18px', fontWeight: '500', marginBottom: '8px', color: '#37352f'}}>
                        No pages yet
                    </div>
                    <div style={{fontSize: '14px', marginBottom: '24px'}}>
                        Create your first page to get started
                    </div>
                    {/* <button
                        onClick={handleCreateNewPage}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: '#37352f',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#ffffff',
                            cursor: 'pointer'
                        }}
                    >
                        Create New Page
                    </button> */}
                </div>
            ) : (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    {profiles.map((profile) => {
                        const statusConfig = getStatusConfig(profile.status);
                        const previewProfile = toPreviewProfile(profile);
                        const isDisabled = actionLoading === profile.id;

                        return (
                            <div
                                key={profile.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '20px 24px',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e8e8e8',
                                    borderRadius: '12px',
                                    transition: 'all 0.15s ease',
                                    position: 'relative',
                                    opacity: isDisabled ? 0.6 : 1
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                    e.currentTarget.style.borderColor = '#d0d0d0';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.borderColor = '#e8e8e8';
                                }}
                            >
                                {/* Preview Thumbnail */}
                                <div style={{
                                    width: '120px',
                                    height: '140px',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    marginRight: '24px',
                                    flexShrink: 0,
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                }}>
                                    <ProfilePreviewCard profile={previewProfile} />
                                </div>

                                {/* Profile Info */}
                                <div style={{flex: 1, minWidth: 0}}>
                                    <div style={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#37352f',
                                        marginBottom: '4px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {profile.settingsJson?.displayName || profile.name || 'Untitled'}
                                    </div>

                                    <div style={{
                                        fontSize: '13px',
                                        color: '#91918e',
                                        marginBottom: '8px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {getProfileUrl(profile)}
                                    </div>

                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                        <div style={{
                                            display: 'inline-block',
                                            padding: '4px 10px',
                                            backgroundColor: statusConfig.bg,
                                            color: statusConfig.color,
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: '500'
                                        }}>
                                            {statusConfig.label}
                                        </div>
                                        {profile.isPublic && profile.status === 'published' && (
                                            <div style={{
                                                display: 'inline-block',
                                                padding: '4px 10px',
                                                backgroundColor: '#e8f4fd',
                                                color: '#0c5460',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '500'
                                            }}>
                                                Public
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginLeft: '20px'
                                }}>
                                    {/* Submit for Review - show for draft/rejected profiles */}
                                    {(profile.status === 'draft' || profile.status === 'rejected') && (
                                        <button
                                            onClick={() => handleSubmitForReview(profile.id)}
                                            disabled={isDisabled}
                                            title="Submit for Review"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '8px 14px',
                                                backgroundColor: '#fff3cd',
                                                border: '1px solid #ffc107',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                color: '#856404',
                                                cursor: isDisabled ? 'wait' : 'pointer',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            <SendIcon />
                                            Submit
                                        </button>
                                    )}

                                    {/* Share Link - show for published profiles */}
                                    {profile.status === 'published' && (
                                        <button
                                            onClick={() => handleShowShareLink(profile)}
                                            title="Share Public Link"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '8px 14px',
                                                backgroundColor: '#e6f4ea',
                                                border: '1px solid #28a745',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                color: '#1e7e34',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            <ShareIcon />
                                            Share
                                        </button>
                                    )}

                                    <button
                                        onClick={() => handleEditPage(profile.id)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '8px 16px',
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            color: '#37352f',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#f7f7f5';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#ffffff';
                                        }}
                                    >
                                        Edit / View
                                    </button>

                                    {/* More Menu */}
                                    <div style={{position: 'relative'}}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setMenuOpen(menuOpen === profile.id ? null : profile.id);
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '32px',
                                                height: '32px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                borderRadius: '6px',
                                                color: '#91918e',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            <MoreIcon />
                                        </button>

                                        {/* Dropdown Menu */}
                                        {menuOpen === profile.id && (
                                            <>
                                                <div
                                                    style={{
                                                        position: 'fixed',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        zIndex: 999
                                                    }}
                                                    onClick={() => setMenuOpen(null)}
                                                />
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    right: 0,
                                                    marginTop: '4px',
                                                    backgroundColor: '#ffffff',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                                    zIndex: 1000,
                                                    minWidth: '160px',
                                                    overflow: 'hidden'
                                                }}>
                                                    <button
                                                        onClick={() => handleOpenRename(profile)}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '10px',
                                                            width: '100%',
                                                            padding: '10px 14px',
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            fontSize: '13px',
                                                            color: '#37352f',
                                                            cursor: 'pointer',
                                                            textAlign: 'left'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        <RenameIcon />
                                                        Rename
                                                    </button>

                                                    <button
                                                        onClick={() => handleDuplicateProfile(profile)}
                                                        disabled={isDisabled}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '10px',
                                                            width: '100%',
                                                            padding: '10px 14px',
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            fontSize: '13px',
                                                            color: '#37352f',
                                                            cursor: isDisabled ? 'wait' : 'pointer',
                                                            textAlign: 'left'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        <CopyIcon />
                                                        Duplicate
                                                    </button>

                                                    {/* Toggle Public Visibility */}
                                                    {profile.status === 'published' && (
                                                        <button
                                                            onClick={() => handleToggleVisibility(profile)}
                                                            disabled={isDisabled}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '10px',
                                                                width: '100%',
                                                                padding: '10px 14px',
                                                                backgroundColor: 'transparent',
                                                                border: 'none',
                                                                fontSize: '13px',
                                                                color: '#37352f',
                                                                cursor: isDisabled ? 'wait' : 'pointer',
                                                                textAlign: 'left'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                            }}
                                                        >
                                                            <ShareIcon />
                                                            {profile.isPublic ? 'Make Private' : 'Make Public'}
                                                        </button>
                                                    )}

                                                    <div style={{
                                                        height: '1px',
                                                        backgroundColor: '#e8e8e8',
                                                        margin: '4px 0'
                                                    }}/>

                                                    <button
                                                        onClick={() => setShowDeleteConfirm(profile.id)}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '10px',
                                                            width: '100%',
                                                            padding: '10px 14px',
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            fontSize: '13px',
                                                            color: '#dc3545',
                                                            cursor: 'pointer',
                                                            textAlign: 'left'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#fef2f2';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        <TrashIcon />
                                                        Delete
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Delete Confirmation Modal */}
                                {showDeleteConfirm === profile.id && (
                                    <>
                                        <div
                                            style={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0,0,0,0.4)',
                                                zIndex: 1000
                                            }}
                                            onClick={() => {
                                                setShowDeleteConfirm(null);
                                                setMenuOpen(null);
                                            }}
                                        />
                                        <div style={{
                                            position: 'fixed',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            backgroundColor: '#ffffff',
                                            borderRadius: '12px',
                                            padding: '24px',
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                                            zIndex: 1001,
                                            width: '400px',
                                            maxWidth: '90vw'
                                        }}>
                                            <h3 style={{
                                                fontSize: '18px',
                                                fontWeight: '600',
                                                color: '#37352f',
                                                marginTop: 0,
                                                marginBottom: '12px'
                                            }}>
                                                Delete Page?
                                            </h3>
                                            <p style={{
                                                fontSize: '14px',
                                                color: '#666666',
                                                marginBottom: '24px'
                                            }}>
                                                Are you sure you want to delete "{profile.settingsJson?.displayName || profile.name}"? This action cannot be undone.
                                            </p>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                gap: '12px'
                                            }}>
                                                <button
                                                    onClick={() => {
                                                        setShowDeleteConfirm(null);
                                                        setMenuOpen(null);
                                                    }}
                                                    style={{
                                                        padding: '10px 20px',
                                                        backgroundColor: '#f5f5f5',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        color: '#37352f',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteProfile(profile.id)}
                                                    style={{
                                                        padding: '10px 20px',
                                                        backgroundColor: '#dc3545',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        color: '#ffffff',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Rename Modal */}
                                {showRenameModal === profile.id && (
                                    <>
                                        <div
                                            style={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0,0,0,0.4)',
                                                zIndex: 1000
                                            }}
                                            onClick={() => {
                                                setShowRenameModal(null);
                                                setRenameValue('');
                                            }}
                                        />
                                        <div style={{
                                            position: 'fixed',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            backgroundColor: '#ffffff',
                                            borderRadius: '12px',
                                            padding: '24px',
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                                            zIndex: 1001,
                                            width: '400px',
                                            maxWidth: '90vw'
                                        }}>
                                            <h3 style={{
                                                fontSize: '18px',
                                                fontWeight: '600',
                                                color: '#37352f',
                                                marginTop: 0,
                                                marginBottom: '16px'
                                            }}>
                                                Rename Page
                                            </h3>
                                            <input
                                                type="text"
                                                value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                placeholder="Enter page name"
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleRenameProfile();
                                                    }
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 14px',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: '8px',
                                                    fontSize: '15px',
                                                    outline: 'none',
                                                    marginBottom: '20px',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                gap: '12px'
                                            }}>
                                                <button
                                                    onClick={() => {
                                                        setShowRenameModal(null);
                                                        setRenameValue('');
                                                    }}
                                                    style={{
                                                        padding: '10px 20px',
                                                        backgroundColor: '#f5f5f5',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        color: '#37352f',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={handleRenameProfile}
                                                    style={{
                                                        padding: '10px 20px',
                                                        backgroundColor: '#37352f',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        color: '#ffffff',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Save
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
